import React from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, Container, Typography, Button } from "@mui/material";
import { theme } from "./theme";
import { Navigation } from "./components/Navigation";
import { RsvpForm } from "./components/RsvpForm";
import { AdminPanel } from "./components/AdminPanel";
import { Login } from "./pages/Login";
import { NotFound } from "./pages/NotFound";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CountdownTimer } from "./components/CountdownTimer";
import { Hero } from "./components/Hero";
import { Trans, useTranslation } from "react-i18next";
import "./i18n";
import { Link } from "react-router-dom";
import { GiftRegistry } from "./pages/GiftRegistry";

const BOOKING_URL =
  "https://boka.hogberga.se/campaign/campaign-details?channelId=a9fab1a0-7065-4b22-b722-fa42a73a1217&culture=sv-SE&campaignId=8d3cd696-6a3e-40f6-9fa2-67125b939c67&promoCode=Josefin+och+Magnus";

function MainContent() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  const sectionTitleSx = {
    typography: "h2",
    mb: 4,
    fontSize: { xs: "2.5rem", md: "3rem" },
    textAlign: "center",
    fontFamily: "'Great Vibes', cursive",
  };

  const contentSx = {
    textAlign: "center",
    maxWidth: "800px",
    mx: "auto",
    fontSize: { xs: "1rem", md: "1.1rem" },
    lineHeight: 1.8,
    fontFamily: "'Raleway', sans-serif",
    px: { xs: 2, md: 0 },
  };

  const toastmastersContent = (
    <Box
      sx={{
        mt: 4,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: { xs: 4, md: 6 },
        justifyItems: "center",
      }}
    >
      <Box sx={{ textAlign: "center" }}>
        <Box
          sx={{
            width: 120,
            height: 120,
            mx: "auto",
            mb: 3,
            borderRadius: "50%",
            border: "3px solid",
            borderColor: "primary.main",
            overflow: "hidden",
            backgroundImage: "url(/images/toastmaster1.jpg)",
            backgroundSize: "280%", // Zoom level: >100% = zoomed in, <100% = zoomed out
            backgroundPosition: "31% 37%", // Adjust these coordinates as needed
            backgroundRepeat: "no-repeat",
          }}
          role="img"
          aria-label={t("sections.speeches.toastmasters.title1")}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            mb: 2,
            fontFamily: "'Raleway', sans-serif",
            fontSize: "1.2rem",
          }}
        >
          {t("sections.speeches.toastmasters.title1")}
        </Typography>
        <Typography sx={{ ...contentSx, fontSize: "1rem", mb: 1 }}>
          {t("sections.speeches.toastmasters.phone1")}
        </Typography>
        <Typography sx={{ ...contentSx, fontSize: "1rem" }}>
          {t("sections.speeches.toastmasters.email1")}
        </Typography>
      </Box>

      <Box sx={{ textAlign: "center" }}>
        <Box
          sx={{
            width: 120,
            height: 120,
            mx: "auto",
            mb: 3,
            borderRadius: "50%",
            border: "3px solid",
            borderColor: "primary.main",
            overflow: "hidden",
            backgroundImage: "url(/images/toastmaster2.jpg)",
            backgroundSize: "190%", // Zoom level: >100% = zoomed in, <100% = zoomed out
            backgroundPosition: "74% 43%", // Adjust these coordinates as needed
            backgroundRepeat: "no-repeat",
          }}
          role="img"
          aria-label={t("sections.speeches.toastmasters.title2")}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            mb: 2,
            fontFamily: "'Raleway', sans-serif",
            fontSize: "1.2rem",
          }}
        >
          {t("sections.speeches.toastmasters.title2")}
        </Typography>
        <Typography sx={{ ...contentSx, fontSize: "1rem", mb: 1 }}>
          {t("sections.speeches.toastmasters.phone2")}
        </Typography>
        <Typography sx={{ ...contentSx, fontSize: "1rem" }}>
          {t("sections.speeches.toastmasters.email2")}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", overflowX: "hidden" }}>
      {/* Hero Section*/}
      <Hero />

      {/* Countdown Section */}
      <Container maxWidth="lg">
        <Box id="countdown" sx={{ py: { xs: 4, md: 8 } }}>
          <CountdownTimer />
        </Box>

        {/* Ceremony Section */}
        <Box id="ceremony" sx={{ py: { xs: 4, md: 4 } }}>
          <Box sx={sectionTitleSx}>{t("sections.ceremony.title")}</Box>
          <Typography sx={contentSx}>
            {t("sections.ceremony.content")}
          </Typography>
        </Box>

        {/* Dress Code Section */}
        <Box id="dress_code" sx={{ py: { xs: 4, md: 4 } }}>
          <Box sx={sectionTitleSx}>{t("sections.dress_code.title")}</Box>
          <Typography sx={contentSx}>
            <Trans i18nKey="sections.dress_code.content" />
          </Typography>
        </Box>

        {/* Gifts Section */}
        <Box
          component="section"
          id="gifts"
          sx={{ py: { xs: 4, md: 4 }, textAlign: "center" }}
        >
          <Box sx={sectionTitleSx}>{t("sections.gifts.title")}</Box>
          <Typography sx={contentSx}>{t("sections.gifts.content")}</Typography>
          <Button
            component={Link}
            to="/wishlist"
            variant="contained"
            color="primary"
            sx={{
              fontFamily: "'Raleway', sans-serif",
              textTransform: "none",
              fontSize: { xs: "1rem", md: "1.1rem" },
              px: { xs: 3, md: 4 },
              py: 1,
              marginTop: 4,
            }}
          >
            {t("sections.gifts.wishlistButton")}
          </Button>
        </Box>

        {/* Speeches Section */}
        <Box id="speeches" sx={{ py: { xs: 4, md: 4 } }}>
          <Box sx={sectionTitleSx}>{t("sections.speeches.title")}</Box>
          <Box
            sx={{
              maxWidth: "800px",
              mx: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Typography sx={contentSx}>
              {t("sections.speeches.intro")}
            </Typography>

            <Typography sx={contentSx}>
              {t("sections.speeches.timing")}
            </Typography>

            {currentUser ? (
              toastmastersContent
            ) : (
              <Typography sx={contentSx}>
                <Trans i18nKey="sections.speeches.toastmasters_unauthenticated" />
              </Typography>
            )}
          </Box>
        </Box>

        {/* Accommodation Section */}
        <Box id="accommodation" sx={{ py: { xs: 4, md: 4 } }}>
          <Box sx={sectionTitleSx}>{t("sections.accommodation.title")}</Box>
          <Typography sx={contentSx}>
            {t("sections.accommodation.content")}{" "}
            {currentUser ? (
              <Trans i18nKey="sections.accommodation.bookingInfo">
                Text <a href={BOOKING_URL}>här</a> text.
              </Trans>
            ) : (
              <Trans i18nKey="sections.accommodation.loginPrompt">
                Vänligen <a href="/login">logga in</a> för att boka rum.
              </Trans>
            )}
          </Typography>
        </Box>

        {/* RSVP Section */}
        <Box id="rsvp" sx={{ py: { xs: 4, md: 4 } }}>
          <Box sx={sectionTitleSx}>{t("sections.rsvp.title")}</Box>
          <Box sx={{ maxWidth: "800px", mx: "auto" }}>
            <RsvpForm />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <Navigation />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminPanel />
                </ProtectedRoute>
              }
            />
            <Route path="/404" element={<NotFound />} />
            <Route path="/" element={<MainContent />} />
            <Route path="/wishlist" element={<GiftRegistry />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
